<template>
    <div class="inline-flex rounded-lg bg-slate-100 dark:bg-slate-700 p-1">
        <button
            v-for="option in options"
            :key="option.value"
            type="button"
            :aria-pressed="preference === option.value"
            class="flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200"
            :class="{
                'bg-white dark:bg-slate-600 text-slate-900 dark:text-slate-100 shadow-sm':
                    preference === option.value,
                'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300':
                    preference !== option.value,
            }"
            @click="setPreference(option.value)"
        >
            <component :is="option.icon" class="w-4 h-4" />
            <span v-if="showLabels">{{ option.label }}</span>
        </button>
    </div>
</template>

<script setup>
import { computed, h } from 'vue';
import { useThemeStore } from '@/store/theme';
import { storeToRefs } from 'pinia';

const props = defineProps({
    showLabels: {
        type: Boolean,
        default: true,
    },
});

const themeStore = useThemeStore();
const { preference } = storeToRefs(themeStore);

const SunIcon = {
    render() {
        return h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                fill: 'none',
                viewBox: '0 0 24 24',
                'stroke-width': '1.5',
                stroke: 'currentColor',
            },
            [
                h('path', {
                    'stroke-linecap': 'round',
                    'stroke-linejoin': 'round',
                    d: 'M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z',
                }),
            ]
        );
    },
};

const MonitorIcon = {
    render() {
        return h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                fill: 'none',
                viewBox: '0 0 24 24',
                'stroke-width': '1.5',
                stroke: 'currentColor',
            },
            [
                h('path', {
                    'stroke-linecap': 'round',
                    'stroke-linejoin': 'round',
                    d: 'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25',
                }),
            ]
        );
    },
};

const MoonIcon = {
    render() {
        return h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                fill: 'none',
                viewBox: '0 0 24 24',
                'stroke-width': '1.5',
                stroke: 'currentColor',
            },
            [
                h('path', {
                    'stroke-linecap': 'round',
                    'stroke-linejoin': 'round',
                    d: 'M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z',
                }),
            ]
        );
    },
};

const options = [
    { value: 'light', label: 'Light', icon: SunIcon },
    { value: 'system', label: 'System', icon: MonitorIcon },
    { value: 'dark', label: 'Dark', icon: MoonIcon },
];

const setPreference = (value) => {
    themeStore.setPreference(value);
};
</script>
